import { Form } from "antd";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { CustomNotification } from "../../../../util/CustomNotification";
import * as commonAPI from "../../../../services/commonServices";
import useValue from "../../../../hooks/useValue";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import type { KeyFobList } from "../../../../shared/interface/MasterManagement/keyFobMaster.interface";
import type { RootState } from "../../../../appRedux/store";

type KeyFobAllocationMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const useKeyFobAllocationMaster = (props: KeyFobAllocationMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isVisitor = queryParams.get("isVisitor");

  /* STATES */
  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);
  const [clientList, setClientList] = useState<{ id: number; client_name: string }[]>([]);
  const [keyFobList, setKeyFobList] = useState<KeyFobList[]>([]);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [userDetails, setUserDetails] = useState<FixMeLater>(null);
  const [customerClientId, setCustomerClientId] = useState<number | null>(null);

  // Adding state for is_visitor checkbox
  const [isVisitorChecked, setIsVisitorChecked] = useState<boolean>(isVisitor ? true : false);

  /* CUSTOM HOOKS */
  const { value: loading, handleDisableValue: handleDisableLoading, handleEnableValue: handleEnableLoading } = useValue(true);
  const { masterFilters } = useSelector(({ common }: RootState) => common);

  /* HANDLERS */

  /* loading states from multiple hooks added as expression */
  const isOnAddLoading = !(props.addRecord && !loading);
  const isOnEditLoading = !(!props.addRecord! && !loading);

  const handleCustomerClientId = (value: number | null) => {
    setCustomerClientId(value);
  };

  const handleChangeClient = (id: number | null) => {
    if (id) handleCustomerClientId(id);
    else handleCustomerClientId(null);
  };

  const resetUserDetailsField = () => {
    form.setFieldsValue({
      first_name: null,
      last_name: null
    });
    setUserDetails(null);
  };

  const handleChangeNumber = (mobileNumber: string) => {
    resetUserDetailsField();
    setMobileNumber(`+91${mobileNumber}`);
  };

  const verifyMobileNumber = async () => {
    try {
      if (!mobileNumber) return;
      const response = await commonAPI.verifyMobileUserAndGetDetails(mobileNumber);

      form.setFieldsValue({
        first_name: response?.data?.["first_name"],
        last_name: response?.data?.["last_name"]
      });

      setUserDetails(response?.data);
    } catch (error) {
      resetUserDetailsField();
      CustomNotification.showErrorMessage(error);
    }
  };

  const handleFinish = (data: any) => {
    if (props.addRecord) {
      props.handleSubmit({ ...data, customer_id: masterFilters?.customerId, customer_location_id: masterFilters?.locationId });
    } else {
      props.handleSubmit({ ...data, customer_id: props.rowData?.customer_id, customer_location_id: props.rowData?.customer_location_id });
    }
  };

  // Handle checkbox change for "is_visitor"
  const handleCheckboxChange = (checked: boolean) => {
    setIsVisitorChecked(checked); // Update the state when checkbox is toggled
  };

  /* EFFECTS */
  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue(props.rowData);
      setMobileNumber(props.rowData?.mobile_number);
      fetchClientList(props.rowData?.customer_id, props.rowData?.customer_location_id);
      fetchKeyFobList(props.rowData?.customer_id, props.rowData?.customer_location_id);
      setFormFieldsLoadedToForm(true);
    }

    handleChangeClient(props?.rowData?.customer_client_id);

    // eslint-disable-next-line
  }, [isOnEditLoading]);

  /* fetchClientList */
  const fetchClientList = async (customerId: number, locationId: number) => {
    try {
      handleEnableLoading();
      const response = await commonAPI.getClientListByCustomerAndLocationId(customerId, locationId);
      setClientList(response?.data);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableLoading();
    }
  };

  useEffect(() => {
    if (props.addRecord) {
      fetchClientList(masterFilters?.customerId!, masterFilters?.locationId!);
    } else {
      fetchClientList(props.rowData?.customer_id, props.rowData?.customer_location_id);
    }

    /* eslint-disable-next-line */
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  /* fetchKeyFobList */
  const fetchKeyFobList = async (customerId: number, locationId: number) => {
    try {
      handleEnableLoading();
      let response;
      if (props?.addRecord) {
        response = await commonAPI.getUnallocatedKeyFobListByCustomerAndLocationId(customerId, locationId);
      } else {
        response = await commonAPI.getKeyFobListByCustomerAndLocationId(customerId, locationId);
      }

      const formattedResponse = response?.data?.map((item) => {
        return {
          ...item,
          keyFob_vehicle_dimension: `${item?.key_fob_token ?? ""} - ${item?.vehicleType?.vehicle_type_name ?? ""} - ${item?.vehicleType?.dimension ?? ""}`
        };
      });

      setKeyFobList(formattedResponse);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisableLoading();
    }
  };

  useEffect(() => {
    if (masterFilters?.customerId && masterFilters?.locationId) fetchKeyFobList(masterFilters?.customerId, masterFilters?.locationId);

    /* eslint-disable-next-line */
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  return {
    formFieldsLoadedToForm,
    userDetails,
    keyFobList,
    clientList,
    form,
    verifyMobileNumber,
    mobileNumber,
    handleChangeNumber,
    isOnAddLoading,
    formRef,
    handleChangeClient,
    customerClientId,
    handleCustomerClientId,
    handleFinish,
    isVisitorChecked,
    handleCheckboxChange
  };
};

export default useKeyFobAllocationMaster;
